import React, {useMemo} from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = {
  pending: '#E6B800',
  completed: '#1FA971',
  failed: '#B3262E',
};

export default function TransactionStatusByHourChart({data = [], title = 'Transaction Status Today By Hour'}) {
  const normalized = useMemo(() => {
    // Ensure 0-23 hours are present
    const map = new Map();
    data.forEach((d) => map.set(d.hour, d));
    const arr = [];
    for (let h = 0; h < 24; h++) {
      arr.push(map.get(h) || {hour: h, pending: 0, completed: 0, failed: 0});
    }
    return arr;
  }, [data]);

  const chartData = useMemo(() => {
    const labels = normalized.map(d => `${d.hour}:00`);
    const statuses = ['pending', 'completed', 'failed'];

    const datasets = statuses.map(status => ({
      label: status.charAt(0).toUpperCase() + status.slice(1),
      data: normalized.map(d => Number(d[status]) || 0),
      backgroundColor: COLORS[status],
      stack: 'stack0',
      borderRadius: 4,
      borderSkipped: false,
      barPercentage: 0.8,
      categoryPercentage: 0.9,
    }));

    return {
      labels,
      datasets,
    };
  }, [normalized]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 20,
          font: {
            size: 11,
            family: "'Inter', sans-serif",
            weight: '500'
          }
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.04)',
          drawBorder: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
            family: "'Inter', sans-serif"
          }
        }
      },
    },
  };

  const hasData = useMemo(() => {
    return normalized.some(d => (d.pending || 0) + (d.completed || 0) + (d.failed || 0) > 0);
  }, [normalized]);

  return (
    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
      <h3 className="text-base font-bold text-gray-900 mb-4">{title}</h3>
      <div className="h-64">
        {!hasData ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">No data</div>
        ) : (
          <Bar options={options} data={chartData} />
        )}
      </div>
    </div>
  );
}
